import { FaUserCircle } from 'react-icons/fa';
import { BsPlus } from 'react-icons/bs';
import cx from 'classnames';
import styles from './Sftp.module.css';
import Button from 'components/Buttons/Button';
import Section, { SectionRow } from 'components/Section/Section';
import TextField from 'components/Inputs/TextField/TextField';
import { Control, UseFormSetValue, useWatch } from 'react-hook-form';
import { SelectFieldWithOthers } from 'components/Inputs/SelectFieldWithOthers/SelectFieldsWithOthers';
import Grid from 'components/Grid/Grid';
import { useEffect, useState } from 'react';
import { useToggle } from 'utils/hooks';
import { FileFormat, UploadSchedule } from 'utils/lookup';

import TimeInput from 'components/Inputs/TimeField/TimeField';
import PasswordField from 'components/Inputs/PasswordField/PasswordField';

type billerUploadSettingCommon = {
	fileFormat: string;
	generationTime: string;
	uploadSched?: string;
	uploadFreq: string;
	uploadTime?: string;
};

export type billerUploadSettings = {
	billerUploadSFTP: billerUploadSFTP;
	billerUploadSMTP: billerUploadSMTP;
};

export type billerUploadSFTP = {
	username?: string;
	password?: string;
	host?: string;
	port?: string;
	path?: string;
	csrConfig?: billerUploadSettingCommon;
	uploadConfig?: billerUploadSettingCommon;
};

export type billerUploadSMTP = {
	senderEmails?: string[];
	receiverEmails?: string[];
	csrConfig?: billerUploadSettingCommon;
	uploadConfig?: billerUploadSettingCommon;
};

type Props = {
	control: Control<any>;
	setValue: UseFormSetValue<any>;
	showOtherIndustry?: boolean;
	showOtherLegalStructure?: boolean;
	disabled?: boolean;
};

function useToggleOthersField(
	value: any,
	predicate: (v: any) => boolean,
	callback?: () => void
) {
	const {
		value: isOthersShown,
		valueOn: showOtherIndustry,
		valueOff: hideOtherIndustry,
	} = useToggle();

	useEffect(() => {
		if (predicate(value)) {
			showOtherIndustry();
		} else {
			hideOtherIndustry();
			callback && callback();
		}
	}, [value]);

	return isOthersShown;
}

const Sftp: React.FC<Props> = ({ control, setValue, disabled }) => {
	return (
		<>
			<Section title="SFTP/FTP">
				<SectionRow verticalAlign="end">
					<Grid column size={1} of={3} className={styles.grid}>
						<TextField
							label="Host"
							required
							disabled={disabled}
							control={control}
							name="sftpInformation.host"
						/>
					</Grid>
					<Grid
						container
						column
						verticalAlign="start"
						size={1}
						of={3}
						className={styles.grid}
					>
						<TextField
							label="Port"
							required
							disabled={disabled}
							control={control}
							name="sftpInformation.port"
						/>
					</Grid>
				</SectionRow>
				<SectionRow>
					<Grid column size={1} of={3}>
						<TextField
							label="Username"
							required
							disabled={disabled}
							control={control}
							name="sftpInformation.username"
						/>
					</Grid>
					<Grid column size={1} of={3}>
						<PasswordField
							label="Password"
							required
							disabled={disabled}
							control={control}
							name="sftpInformation.password"
						/>
					</Grid>
					<Grid column size={1} of={3}>
						<TextField
							label="Path"
							required
							disabled={disabled}
							control={control}
							name="sftpInformation.path"
						/>
					</Grid>
				</SectionRow>
			</Section>

			<Section title="Collection Summary Report (CSR) Configuration">
				<SectionRow verticalAlign="end">
					<Grid column size={1} of={3} className={styles.grid}>
						<SelectFieldWithOthers
							label="Filename Format"
							disabled={disabled}
							required
							control={control}
							name="sftpInformation.csrConfigFileFormat"
							options={FileFormat.map((i) => ({
								label: i,
								value: i,
							}))}
						/>
					</Grid>
					<Grid
						container
						column
						verticalAlign="start"
						size={1}
						of={3}
						className={styles.grid}
					>
						<SelectFieldWithOthers
							label="Upload Schedule"
							disabled={disabled}
							required
							control={control}
							name="sftpInformation.csrConfigUploadSched"
							options={UploadSchedule}
						/>
					</Grid>
				</SectionRow>
				<SectionRow>
					<Grid column size={1} of={3} className={styles.grid}>
						<TimeInput
							label="Generation Time"
							required
							disabled={disabled}
							control={control}
							name="sftpInformation.csrConfigGenerationTime"
						/>
					</Grid>
					<Grid column size={1} of={3} className={styles.grid}>
						<TextField
							label="Upload Frequency"
							required
							disabled={disabled}
							control={control}
							name="sftpInformation.csrConfigUploadFreq"
						/>
					</Grid>
					<Grid column size={1} of={3} className={styles.grid}>
						<TimeInput
							label="Time of Upload"
							required
							disabled={disabled}
							control={control}
							name="sftpInformation.csrConfigUploadTime"
						/>
					</Grid>
				</SectionRow>
			</Section>

			<Section title="Select Columns for the CSR Report Template">
				<SectionRow verticalAlign="end"></SectionRow>
			</Section>

			<Section title="Upload Configuration">
				<SectionRow verticalAlign="end">
					<Grid column size={1} of={3} className={styles.grid}>
						<SelectFieldWithOthers
							label="Filename Format"
							disabled={disabled}
							required
							control={control}
							name="sftpInformation.uploadConfigFileFormat"
							options={FileFormat.map((i) => ({
								label: i,
								value: i,
							}))}
						/>
					</Grid>
					<Grid
						container
						column
						verticalAlign="start"
						size={1}
						of={3}
						className={styles.grid}
					>
						<SelectFieldWithOthers
							label="Upload Schedule"
							disabled={disabled}
							required
							control={control}
							name="sftpInformation.uploadConfigUploadSched"
							options={UploadSchedule}
						/>
					</Grid>
				</SectionRow>
				<SectionRow>
					<Grid column size={1} of={3} className={styles.grid}>
						<TimeInput
							label="Generation Time"
							required
							disabled={disabled}
							control={control}
							name="sftpInformation.uploadConfigGenerationTime"
						/>
					</Grid>
					<Grid column size={1} of={3} className={styles.grid}>
						<TextField
							label="Upload Frequency"
							required
							disabled={disabled}
							control={control}
							name="sftpInformation.uploadConfigUploadFreq"
						/>
					</Grid>
					<Grid column size={1} of={3} className={styles.grid}>
						<TimeInput
							label="Time of Upload"
							required
							disabled={disabled}
							control={control}
							name="sftpInformation.uploadConfigUploadTime"
						/>
					</Grid>
				</SectionRow>
			</Section>
		</>
	);
};

export default Sftp;
